import { AnalyticsConfig } from '../types';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    _fbq?: any;
    clarity?: (...args: any[]) => void;
  }
}

const env = (import.meta as any).env || {};

let currentConfig: AnalyticsConfig = {
  googleAnalyticsId: env.VITE_GA_MEASUREMENT_ID || '',
  isGoogleAnalyticsEnabled: Boolean(env.VITE_GA_MEASUREMENT_ID),
  facebookPixelId: env.VITE_FB_PIXEL_ID || '',
  isFacebookPixelEnabled: Boolean(env.VITE_FB_PIXEL_ID),
  clarityProjectId: env.VITE_CLARITY_PROJECT_ID || '',
  isClarityEnabled: Boolean(env.VITE_CLARITY_PROJECT_ID),
  trackPageViews: true,
  trackPurchases: true,
  trackCheckouts: true,
};

let loadedGaId: string | null = null;
let loadedFbId: string | null = null;
let loadedClarityId: string | null = null;

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => {
      console.warn(`[Analytics] Failed to load ${src}`);
      resolve();
    };
    document.head.appendChild(script);
  });
}

function initGoogleAnalytics(id: string) {
  if (!id || id.trim() === '' || loadedGaId === id) return;
  loadedGaId = id;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: false });
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${id}`, 'ga-script-tag');
}

function initFacebookPixel(id: string) {
  if (!id || id.trim() === '' || loadedFbId === id) return;
  loadedFbId = id;
  if (!window.fbq) {
    const fbq: any = function () {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
    loadScript('https://connect.facebook.net/en_US/fbevents.js', 'fb-pixel-tag');
  }
  try {
    window.fbq('init', id);
  } catch (err) {
    console.warn('[Analytics] Meta Pixel initialization error', err);
  }
}

function initMicrosoftClarity(id: string) {
  if (!id || id.trim() === '' || loadedClarityId === id) return;
  loadedClarityId = id;
  if (!window.clarity) {
    window.clarity = function () {
      ((window.clarity as any).q = (window.clarity as any).q || []).push(arguments);
    };
  }
  loadScript(`https://www.clarity.ms/tag/${id}`, 'clarity-script-tag');
}

export function initAnalytics(config?: AnalyticsConfig) {
  if (config) currentConfig = { ...currentConfig, ...config };
  if (currentConfig.isGoogleAnalyticsEnabled && currentConfig.googleAnalyticsId) {
    initGoogleAnalytics(currentConfig.googleAnalyticsId);
  }
  if (currentConfig.isFacebookPixelEnabled && currentConfig.facebookPixelId) {
    initFacebookPixel(currentConfig.facebookPixelId);
  }
  if (currentConfig.isClarityEnabled && currentConfig.clarityProjectId) {
    initMicrosoftClarity(currentConfig.clarityProjectId);
  }
}

export function trackPageView(pageTitle: string, pagePath?: string) {
  if (!currentConfig.trackPageViews) return;
  const path = pagePath || window.location.pathname;
  if (currentConfig.isGoogleAnalyticsEnabled && window.gtag) {
    window.gtag('event', 'page_view', { page_title: pageTitle, page_path: path });
  }
  if (currentConfig.isFacebookPixelEnabled && window.fbq) {
    window.fbq('track', 'PageView');
  }
  if (currentConfig.isClarityEnabled && window.clarity) {
    window.clarity('set', 'page', pageTitle);
  }
}

export function trackCheckoutInitiated(planName: string, amount: number, currency: string = 'INR') {
  if (!currentConfig.trackCheckouts) return;
  if (currentConfig.isGoogleAnalyticsEnabled && window.gtag) {
    window.gtag('event', 'begin_checkout', {
      value: amount,
      currency,
      items: [{ item_name: planName, price: amount, quantity: 1 }],
    });
  }
  if (currentConfig.isFacebookPixelEnabled && window.fbq) {
    window.fbq('track', 'InitiateCheckout', { content_name: planName, value: amount, currency });
  }
  if (currentConfig.isClarityEnabled && window.clarity) {
    window.clarity('event', 'begin_checkout');
  }
}

export function trackPurchase(
  orderId: string,
  planName: string,
  amount: number,
  currency: string = 'INR',
  customerEmail?: string
) {
  if (!currentConfig.trackPurchases) return;
  if (currentConfig.isGoogleAnalyticsEnabled && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: orderId,
      value: amount,
      currency,
      items: [{ item_name: planName, price: amount, quantity: 1 }],
    });
  }
  if (currentConfig.isFacebookPixelEnabled && window.fbq) {
    window.fbq('track', 'Purchase', { content_name: planName, value: amount, currency, order_id: orderId });
  }
  if (currentConfig.isClarityEnabled && window.clarity) {
    window.clarity('event', 'purchase');
    if (customerEmail) window.clarity('set', 'customer_email', customerEmail);
  }
}

export function trackCustomEvent(eventName: string, params?: Record<string, any>) {
  if (currentConfig.isGoogleAnalyticsEnabled && window.gtag) {
    window.gtag('event', eventName, params);
  }
  if (currentConfig.isFacebookPixelEnabled && window.fbq) {
    window.fbq('trackCustom', eventName, params);
  }
  if (currentConfig.isClarityEnabled && window.clarity) {
    window.clarity('event', eventName);
  }
}

export function identifyAnalyticsUser(userId: string, email?: string, name?: string) {
  if (currentConfig.isGoogleAnalyticsEnabled && window.gtag) {
    window.gtag('set', 'user_properties', { user_id: userId, user_email: email });
  }
  if (currentConfig.isClarityEnabled && window.clarity) {
    window.clarity('identify', userId, undefined, undefined, name || email || userId);
  }
}
